/**
 * VatDashboard — Accounts › VAT   (/accounts/vat)
 *
 * The VAT ledger for a period (every sale and purchase with its VAT), the UK
 * VAT Return boxes 1–9 the period produces, a filing-ready CSV, the current
 * credit/debit position across unfiled periods, and a register of periods
 * marked as filed so nothing is counted twice. Figures come straight from
 * recorded invoices; nothing here changes them.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, HStack, VStack, Text, Select, Badge, Table, Thead, Tbody, Tr, Th, Td, IconButton, Spinner, useToast, SimpleGrid, Input, Tabs, TabList, Tab, TabPanels, TabPanel, Tooltip } from '@chakra-ui/react';
import { FiArrowLeft, FiDownload, FiCheckCircle, FiRefreshCw } from 'react-icons/fi';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

const money = (v) => (v == null || isNaN(+v) ? '—' : (+v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const whole = (v) => (v == null || isNaN(+v) ? '—' : Math.round(+v).toLocaleString('en-GB'));

export default function VatDashboard({ onBack }) {
  const toast = useToast();
  const { user } = useAuth();
  const [periods, setPeriods] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [sel, setSel] = useState(null);           // { from, to }
  const [custom, setCustom] = useState({ from: '', to: '' });
  const [summary, setSummary] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [position, setPosition] = useState(null);
  const [filings, setFilings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ref, setRef] = useState('');

  useEffect(() => {
    api.get('/vat/periods').then(r => { setCfg(r.data); setPeriods(r.data.periods || []); const cur = (r.data.periods || []).find(p => p.current) || (r.data.periods || [])[0]; if (cur) setSel({ from: cur.from, to: cur.to }); }).catch(() => {});
    api.get('/vat/position').then(r => setPosition(r.data)).catch(() => {});
    api.get('/vat/filings').then(r => setFilings(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!sel) return;
    setLoading(true);
    try {
      const [s, l] = await Promise.all([api.get(`/vat/summary?from=${sel.from}&to=${sel.to}`), api.get(`/vat/ledger?from=${sel.from}&to=${sel.to}`)]);
      setSummary(s.data); setLedger(l.data || []);
    } catch (e) { toast({ title: 'Could not load the period', description: e?.response?.data?.error, status: 'error', duration: 3000 }); }
    setLoading(false);
  }, [sel, toast]);
  useEffect(() => { load(); }, [load]);

  const download = async () => {
    try {
      const r = await api.get(`/vat/return.csv?from=${sel.from}&to=${sel.to}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url; a.download = `vat-return-${sel.from}-to-${sel.to}.csv`; document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast({ title: 'Export failed', status: 'error', duration: 2500 }); }
  };
  const markFiled = async () => {
    if (!window.confirm(`Record the period ${sel.from} to ${sel.to} as filed with HMRC? The nine box values are frozen on the register.`)) return;
    try { await api.post('/vat/filings', { from: sel.from, to: sel.to, reference: ref || undefined }); toast({ title: 'Period recorded as filed', status: 'success', duration: 2000 }); setRef(''); load(); api.get('/vat/filings').then(r => setFilings(r.data || [])); api.get('/vat/position').then(r => setPosition(r.data)); }
    catch (e) { toast({ title: e?.response?.data?.error || 'Failed', status: 'error', duration: 3000 }); }
  };

  const BoxCard = ({ n, label, value, big, wholeNum }) => (
    <Box p={3} bg="white" border="1px solid" borderColor={n === 5 ? (value >= 0 ? 'red.200' : 'green.200') : 'gray.200'} borderRadius="md">
      <HStack justify="space-between"><Badge colorScheme={n === 5 ? (value >= 0 ? 'red' : 'green') : 'gray'}>Box {n}</Badge></HStack>
      <Text fontSize="10px" color="gray.600" mt={1} minH="28px">{label}</Text>
      <Text fontSize={big ? '2xl' : 'lg'} fontWeight="700" textAlign="right">{wholeNum ? whole(value) : money(value)}</Text>
    </Box>
  );

  const sales = ledger.filter(r => r.direction === 'out'), purchases = ledger.filter(r => r.direction === 'in');
  const Row = ({ r }) => (
    <Tr>
      <Td fontSize="xs">{String(r.issue_date).slice(0, 10)}</Td>
      <Td fontSize="xs">{r.counterparty || '—'}{r.document_type === 'credit_note' && <Badge ml={1} colorScheme="purple" fontSize="8px">credit note</Badge>}</Td>
      <Td fontSize="xs">{r.invoice_number || '—'}</Td>
      <Td fontSize="xs" color="gray.600">{r.project_name}{r.task_title ? ` › ${r.task_title}` : ''}</Td>
      <Td fontSize="xs" isNumeric>{money(r.total_net)}</Td>
      <Td fontSize="xs" isNumeric>{money(r.total_vat)}</Td>
      <Td fontSize="xs" isNumeric fontWeight="600">{money(r.vat_for_return)}{r.direction === 'in' && Math.abs((+r.total_vat || 0) - (+r.vat_for_return || 0)) > 0.005 && <Tooltip label={`VAT on the invoice ${money(r.total_vat)}; ${r.vat_treatment || 'not claimable'}`}><Text as="span" color="orange.600"> *</Text></Tooltip>}</Td>
      <Td fontSize="xs" isNumeric>{money(r.total_gross)}</Td>
      <Td fontSize="xs">{String(r.status || '').replace(/^recv_/, '').replace(/_/g, ' ')}</Td>
    </Tr>
  );

  return (
    <Box p={6} maxW="1300px" mx="auto">
      <HStack mb={4} spacing={3} wrap="wrap">
        <IconButton icon={<FiArrowLeft />} aria-label="back" variant="ghost" onClick={onBack} />
        <Text fontSize="xl" fontWeight="700">VAT</Text>
        {cfg && <Badge colorScheme="gray">{cfg.scheme} accounting · {cfg.months}-month periods{cfg.vat_number ? ` · VAT ${cfg.vat_number}` : ''}</Badge>}
        <Box flex="1" />
        <Select size="sm" maxW="280px" value={sel ? `${sel.from}|${sel.to}` : ''} onChange={e => { const [from, to] = e.target.value.split('|'); setSel({ from, to }); }}>
          {periods.map(p => <option key={p.from} value={`${p.from}|${p.to}`}>{p.label}{p.current ? ' (current)' : ''}</option>)}
          {sel && !periods.some(p => p.from === sel.from && p.to === sel.to) && <option value={`${sel.from}|${sel.to}`}>{sel.from} – {sel.to}</option>}
        </Select>
        <HStack spacing={1}><Input size="sm" type="date" value={custom.from} onChange={e => setCustom(c => ({ ...c, from: e.target.value }))} w="150px" /><Text fontSize="xs">to</Text><Input size="sm" type="date" value={custom.to} onChange={e => setCustom(c => ({ ...c, to: e.target.value }))} w="150px" /><Button size="sm" variant="outline" isDisabled={!custom.from || !custom.to} onClick={() => setSel({ from: custom.from, to: custom.to })}>Go</Button></HStack>
        <IconButton size="sm" variant="ghost" icon={<FiRefreshCw />} aria-label="refresh" onClick={load} />
      </HStack>

      {position && (
        <Box mb={4} p={4} bg={position.position >= 0 ? 'red.50' : 'green.50'} border="1px solid" borderColor={position.position >= 0 ? 'red.200' : 'green.200'} borderRadius="md">
          <HStack justify="space-between" wrap="wrap">
            <Box>
              <Text fontSize="xs" fontWeight="700" textTransform="uppercase" color="gray.600">Current VAT position</Text>
              <Text fontSize="2xl" fontWeight="800" color={position.position >= 0 ? 'red.700' : 'green.700'}>{position.position >= 0 ? 'Payable' : 'Repayable'} £{money(Math.abs(position.position))}</Text>
              <Text fontSize="xs" color="gray.600">{position.unfiled_periods} unfiled past period{position.unfiled_periods === 1 ? '' : 's'} (£{money(position.owed_unfiled)}) + current period to date (£{money(position.current_period_to_date)}). {position.note}</Text>
            </Box>
            <VStack align="stretch" spacing={0} fontSize="xs">
              {position.periods.slice(0, 5).map(p => <HStack key={p.from} justify="space-between" spacing={4}><Text color="gray.600">{p.label}</Text><Text fontWeight="600" color={p.box5 >= 0 ? 'red.700' : 'green.700'}>{money(p.box5)}</Text><Badge colorScheme={p.filed ? 'green' : p.current ? 'blue' : 'orange'} fontSize="8px">{p.filed ? 'filed' : p.current ? 'current' : 'not filed'}</Badge></HStack>)}
            </VStack>
          </HStack>
        </Box>
      )}

      {loading && !summary ? <Spinner /> : summary && (
        <>
          <HStack mb={2} justify="space-between" wrap="wrap">
            <Text fontSize="sm" fontWeight="700">VAT Return boxes — {summary.from} to {summary.to}{summary.filed && <Badge ml={2} colorScheme="green"><FiCheckCircle style={{ display: 'inline' }} /> filed {String(summary.filed.filed_at).slice(0, 10)}{summary.filed.reference ? ` · ${summary.filed.reference}` : ''}</Badge>}</Text>
            <HStack>
              <Button size="sm" leftIcon={<FiDownload />} onClick={download}>Download return CSV</Button>
              {user?.role === 'admin' && !summary.filed && <HStack><Input size="sm" placeholder="HMRC reference (optional)" value={ref} onChange={e => setRef(e.target.value)} w="200px" /><Button size="sm" colorScheme="green" leftIcon={<FiCheckCircle />} onClick={markFiled}>Mark period filed</Button></HStack>}
            </HStack>
          </HStack>
          <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} spacing={2} mb={2}>
            <BoxCard n={1} label="VAT due on sales and other outputs" value={summary.box1} />
            <BoxCard n={2} label="VAT due on acquisitions from EU (NI only)" value={summary.box2} />
            <BoxCard n={3} label="Total VAT due (1 + 2)" value={summary.box3} />
            <BoxCard n={4} label="VAT reclaimed on purchases and other inputs" value={summary.box4} />
            <BoxCard n={5} label="Net VAT to pay HMRC (or reclaim if negative)" value={summary.box5} big />
          </SimpleGrid>
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={2} mb={2}>
            <BoxCard n={6} label="Total sales excluding VAT (whole £)" value={summary.box6} wholeNum />
            <BoxCard n={7} label="Total purchases excluding VAT (whole £)" value={summary.box7} wholeNum />
            <BoxCard n={8} label="Supplies of goods to EU (NI only)" value={summary.box8} wholeNum />
            <BoxCard n={9} label="Acquisitions of goods from EU (NI only)" value={summary.box9} wholeNum />
          </SimpleGrid>
          <Text fontSize="10px" color="gray.500" mb={4}>{summary.sales_count} sales, {summary.purchase_count} purchases ({summary.credit_notes} credit notes). VAT on purchases not reclaimable (reverse charge, non-VAT invoices, missing supplier VAT number): £{money(summary.not_claimable)} — marked * in the ledger. Boxes 2, 8 and 9 apply to Northern Ireland goods only and are reported as 0. Figures use invoice dates; GBP invoices only.</Text>

          <Tabs size="sm" variant="enclosed">
            <TabList><Tab>Sales — output VAT ({sales.length})</Tab><Tab>Purchases — input VAT ({purchases.length})</Tab><Tab>Filings ({filings.length})</Tab></TabList>
            <TabPanels>
              {[sales, purchases].map((rows, i) => (
                <TabPanel key={i} px={0}>
                  <Box overflowX="auto" bg="white" border="1px solid" borderColor="gray.200" borderRadius="md">
                    <Table size="sm"><Thead><Tr><Th>Date</Th><Th>{i === 0 ? 'Customer' : 'Supplier'}</Th><Th>Number</Th><Th>Project</Th><Th isNumeric>Net</Th><Th isNumeric>VAT</Th><Th isNumeric>{i === 0 ? 'Output VAT' : 'Reclaimable'}</Th><Th isNumeric>Gross</Th><Th>Status</Th></Tr></Thead>
                      <Tbody>{rows.map(r => <Row key={r.id} r={r} />)}{rows.length === 0 && <Tr><Td colSpan={9}><Text fontSize="xs" color="gray.500">None in this period.</Text></Td></Tr>}</Tbody>
                    </Table>
                  </Box>
                </TabPanel>
              ))}
              <TabPanel px={0}>
                <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" overflowX="auto">
                  <Table size="sm"><Thead><Tr><Th>Period</Th><Th>Filed</Th><Th>By</Th><Th>Reference</Th><Th isNumeric>Box 1</Th><Th isNumeric>Box 4</Th><Th isNumeric>Box 5</Th></Tr></Thead>
                    <Tbody>{filings.map(f => <Tr key={f.id}><Td fontSize="xs">{String(f.period_from).slice(0, 10)} – {String(f.period_to).slice(0, 10)}</Td><Td fontSize="xs">{String(f.filed_at).slice(0, 10)}</Td><Td fontSize="xs">{f.filed_by_name || '—'}</Td><Td fontSize="xs">{f.reference || '—'}</Td><Td fontSize="xs" isNumeric>{money(f.boxes?.box1)}</Td><Td fontSize="xs" isNumeric>{money(f.boxes?.box4)}</Td><Td fontSize="xs" isNumeric fontWeight="700">{money(f.boxes?.box5)}</Td></Tr>)}{filings.length === 0 && <Tr><Td colSpan={7}><Text fontSize="xs" color="gray.500">No periods marked as filed yet.</Text></Td></Tr>}</Tbody>
                  </Table>
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </>
      )}
    </Box>
  );
}
